import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  Shield, CreditCard, CheckCircle2, Clock, Send, 
  RefreshCw, Search, Mail, Phone, IndianRupee,
  AlertCircle, Eye, EyeOff, Copy, Check, Users,
  FileText, FileSpreadsheet, Star, MessageSquare, Calendar,
  TrendingUp, UserPlus, Download, GraduationCap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [gstLeads, setGstLeads] = useState([]);
  const [tdsLeads, setTdsLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sendingCredentials, setSendingCredentials] = useState({});
  const [showPassword, setShowPassword] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [exportingExcel, setExportingExcel] = useState(false);
  
  // Feedback modal
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(0);

  const handleAdminLogin = () => {
    if (adminPassword === 'TaxSetu@Admin2026') {
      setIsAuthenticated(true);
      localStorage.setItem('adminAuth', 'true');
    } else {
      setAuthError('Invalid admin password');
    }
  };

  useEffect(() => {
    if (localStorage.getItem('adminAuth') === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/admin/dashboard-stats`);
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
      
      // Fetch GST Registration leads
      const leadsRes = await fetch(`${API_URL}/api/admin/gst-registrations`);
      if (leadsRes.ok) {
        const leadsData = await leadsRes.json();
        setGstLeads(leadsData.leads || []);
      }
      
      // Fetch TDS Registration leads
      const tdsLeadsRes = await fetch(`${API_URL}/api/admin/tds-registrations`);
      if (tdsLeadsRes.ok) {
        const tdsLeadsData = await tdsLeadsRes.json();
        setTdsLeads(tdsLeadsData.leads || []);
      }
      
      // Fetch all payments
      const paymentsRes = await fetch(`${API_URL}/api/admin/payments`);
      if (paymentsRes.ok) {
        const paymentsData = await paymentsRes.json();
        setPayments(paymentsData.payments || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardData();
    }
  }, [isAuthenticated]);

  const handleSendCredentials = async (paymentId) => {
    setSendingCredentials(prev => ({ ...prev, [paymentId]: true }));
    try {
      const response = await fetch(`${API_URL}/api/admin/verify-and-send-credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_id: paymentId })
      });
      if (response.ok) {
        await fetchDashboardData();
        alert('Credentials sent successfully!');
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      alert('Failed to send credentials. Please try again.');
    } finally {
      setSendingCredentials(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  const handleAddFeedback = async () => {
    if (!selectedLead || !feedbackText) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/add-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_id: selectedLead._id,
          feedback: feedbackText,
          rating: feedbackRating || null
        })
      });
      
      if (response.ok) {
        setShowFeedbackModal(false);
        setFeedbackText('');
        setFeedbackRating(0);
        setSelectedLead(null);
        await fetchDashboardData();
        alert('Feedback added successfully!');
      }
    } catch (error) {
      alert('Failed to add feedback');
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/export-leads/excel`);
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PractiQ_GST_Leads_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      alert('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export Excel. Please try again.');
    } finally {
      setExportingExcel(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format date as DD/MM/YYYY
  const formatDateOnly = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Format time as HH:MM AM/PM
  const formatTimeOnly = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Filter payments by type
  const gstr1Payments = payments.filter(p => p.plan === 'gstr1');
  const gstr3bPayments = payments.filter(p => p.plan === 'gstr3b');
  const bothPayments = payments.filter(p => ['complete', 'bundle', 'both'].includes(p.plan));

  // Filter PW Batch students (from /pw page)
  const pwBatchLeads = gstLeads.filter(lead => lead.source === 'pw_batch_gst_registration');
  
  // Filter leads based on search
  const filteredLeads = gstLeads.filter(lead =>
    lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter PW leads based on search
  const filteredPWLeads = pwBatchLeads.filter(lead =>
    lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Admin Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center px-4">
        <Card className="bg-[#111827] border-white/10 w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-10 h-10 text-amber-400" />
            </div>
            <CardTitle className="text-2xl text-white">Admin Portal</CardTitle>
            <p className="text-gray-400 text-sm mt-2">PractiQ | TaxSetu AI</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Input
                type="password"
                placeholder="Enter admin password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdminLogin()}
                className="bg-white/5 border-white/10 text-white h-12"
              />
              {authError && (
                <p className="text-red-400 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {authError}
                </p>
              )}
              <Button onClick={handleAdminLogin} className="w-full h-12 bg-amber-600 hover:bg-amber-500">
                <Shield className="w-4 h-4 mr-2" />
                Access Admin Panel
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#0A0F1C] py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Shield className="w-8 h-8 text-amber-400" />
              Admin Dashboard
            </h1>
            <p className="text-gray-400 mt-1">PractiQ - User Management</p>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={fetchDashboardData} variant="outline" className="border-white/20 text-white hover:bg-white/10">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={() => { localStorage.removeItem('adminAuth'); setIsAuthenticated(false); }} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
              Logout
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border-emerald-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-400 text-sm font-medium">GST Registrations</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.gst_registrations || 0}</p>
                  <p className="text-emerald-300/70 text-xs mt-1">Free Users</p>
                </div>
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <UserPlus className="w-6 h-6 text-emerald-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 border-cyan-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-cyan-400 text-sm font-medium">TDS Registrations</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.tds_registrations || 0}</p>
                  <p className="text-cyan-300/70 text-xs mt-1">TDS Users</p>
                </div>
                <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-cyan-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-400 text-sm font-medium">GSTR-1 Users</p>
                  <p className="text-3xl font-bold text-white mt-1">{gstr1Payments.length}</p>
                  <p className="text-blue-300/70 text-xs mt-1">Paid</p>
                </div>
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-400 text-sm font-medium">GSTR-3B Users</p>
                  <p className="text-3xl font-bold text-white mt-1">{gstr3bPayments.length}</p>
                  <p className="text-purple-300/70 text-xs mt-1">Paid</p>
                </div>
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                  <FileSpreadsheet className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-400 text-sm font-medium">Both (Bundle)</p>
                  <p className="text-3xl font-bold text-white mt-1">{bothPayments.length}</p>
                  <p className="text-amber-300/70 text-xs mt-1">Premium Users</p>
                </div>
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Overview', icon: Shield },
            { id: 'pw-batch', label: 'PW Batch Students', icon: GraduationCap, count: pwBatchLeads.length },
            { id: 'gst-reg', label: 'GST Registration', icon: UserPlus, count: gstLeads.length },
            { id: 'tds-reg', label: 'TDS Registration', icon: FileText, count: tdsLeads.length },
            { id: 'gstr1', label: 'GSTR-1', icon: FileText, count: gstr1Payments.length },
            { id: 'gstr3b', label: 'GSTR-3B', icon: FileSpreadsheet, count: gstr3bPayments.length },
            { id: 'both', label: 'Both', icon: TrendingUp, count: bothPayments.length },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== undefined && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-teal-500/30 text-teal-300' : 'bg-white/10 text-gray-400'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Search by email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white"
            />
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'overview' && (
          <Card className="bg-[#111827] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-teal-400" />
                Recent Payments
              </CardTitle>
            </CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No payments yet</p>
              ) : (
                <div className="space-y-3">
                  {payments.slice(0, 10).map((payment) => (
                    <div key={payment._id} className="p-4 bg-white/5 rounded-lg border border-white/10">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-teal-500/20 rounded-lg flex items-center justify-center">
                            <Mail className="w-5 h-5 text-teal-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{payment.email}</p>
                            <p className="text-gray-500 text-sm">{payment.planName || payment.plan}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={payment.status === 'verified' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}>
                            {payment.status === 'verified' ? 'Verified' : 'Pending'}
                          </Badge>
                          {payment.status !== 'verified' && (
                            <Button
                              size="sm"
                              onClick={() => handleSendCredentials(payment._id)}
                              disabled={sendingCredentials[payment._id]}
                              className="bg-teal-600 hover:bg-teal-500"
                            >
                              {sendingCredentials[payment._id] ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Send className="w-4 h-4 mr-1" />
                                  Send Credentials
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'gst-reg' && (
          <Card className="bg-[#111827] border-white/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-emerald-400" />
                  GST Registration Leads (Free)
                  <Badge className="bg-emerald-500/20 text-emerald-400 ml-2">{filteredLeads.length} unique users</Badge>
                </CardTitle>
                <Button 
                  onClick={handleExportExcel}
                  disabled={exportingExcel || filteredLeads.length === 0}
                  className="bg-emerald-600 hover:bg-emerald-500"
                  data-testid="export-excel-btn"
                >
                  {exportingExcel ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Export Excel
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {filteredLeads.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No GST registrations yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">#</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Name</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Email</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Batch</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Date</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Time</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Visits</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Rating</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Feedback</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLeads.map((lead, index) => (
                        <tr key={lead._id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-3 px-4 text-gray-500">{index + 1}</td>
                          <td className="py-3 px-4 text-white">{lead.name || '-'}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span className="text-teal-400">{lead.email}</span>
                              <button
                                onClick={() => copyToClipboard(lead.email, lead._id)}
                                className="text-gray-500 hover:text-white"
                              >
                                {copiedId === lead._id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                              </button>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-sm">
                              <span className="text-white">{lead.batchName || '-'}</span>
                              {lead.batchNumber && (
                                <span className="text-gray-500 ml-1">({lead.batchNumber})</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-300 text-sm font-mono">{formatDateOnly(lead.created_at)}</td>
                          <td className="py-3 px-4 text-gray-400 text-sm">{formatTimeOnly(lead.created_at)}</td>
                          <td className="py-3 px-4">
                            <Badge className="bg-blue-500/20 text-blue-400">
                              {lead.visit_count || 1} {(lead.visit_count || 1) === 1 ? 'visit' : 'visits'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            {lead.rating ? (
                              <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                  <Star 
                                    key={i}
                                    className={`w-3 h-3 ${i < lead.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`}
                                  />
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-600">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {(lead.feedback_liked || lead.feedback_improve) ? (
                              <div className="max-w-[200px]">
                                {lead.feedback_liked && (
                                  <p className="text-green-400 text-xs truncate" title={lead.feedback_liked}>
                                    👍 {lead.feedback_liked}
                                  </p>
                                )}
                                {lead.feedback_improve && (
                                  <p className="text-amber-400 text-xs truncate" title={lead.feedback_improve}>
                                    💡 {lead.feedback_improve}
                                  </p>
                                )}
                              </div>
                            ) : lead.feedback ? (
                              <span className="text-gray-300 text-xs truncate max-w-[150px] block">{lead.feedback}</span>
                            ) : (
                              <span className="text-gray-600 text-sm">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedLead(lead);
                                  setFeedbackText(lead.feedback || '');
                                  setFeedbackRating(lead.rating || 0);
                                  setShowFeedbackModal(true);
                                }}
                                className="border-white/20 text-white hover:bg-white/10"
                              >
                                <MessageSquare className="w-4 h-4" />
                              </Button>
                              {lead.activity_log && lead.activity_log.length > 0 && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedLead(lead);
                                    setShowActivityModal(true);
                                  }}
                                  className="border-white/20 text-white hover:bg-white/10"
                                >
                                  <Calendar className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* PW Batch Students Tab */}
        {activeTab === 'pw-batch' && (
          <Card className="bg-[#111827] border-white/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-purple-400" />
                  PW Batch Students
                  <Badge className="bg-purple-500/20 text-purple-400 ml-2">{filteredPWLeads.length} students</Badge>
                </CardTitle>
                <Button 
                  onClick={handleExportExcel}
                  disabled={exportingExcel || filteredPWLeads.length === 0}
                  className="bg-purple-600 hover:bg-purple-500"
                  data-testid="export-pw-excel-btn"
                >
                  {exportingExcel ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Export Excel
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {filteredPWLeads.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No PW batch students yet. Students will appear here after registering from /pw page.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">#</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Name</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Email</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Batch Name</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Batch No.</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Date</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Time</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Visits</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Rating</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Feedback</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPWLeads.map((lead, index) => (
                        <tr key={lead._id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-3 px-4 text-gray-500">{index + 1}</td>
                          <td className="py-3 px-4 text-white font-medium">{lead.name || '-'}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span className="text-purple-400">{lead.email}</span>
                              <button
                                onClick={() => copyToClipboard(lead.email, lead._id)}
                                className="text-gray-500 hover:text-white"
                              >
                                {copiedId === lead._id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                              </button>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className="bg-purple-500/20 text-purple-400">
                              {lead.batchName || 'CPFTA'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-white">{lead.batchNumber || '-'}</td>
                          <td className="py-3 px-4 text-gray-300 text-sm font-mono">{formatDateOnly(lead.created_at)}</td>
                          <td className="py-3 px-4 text-gray-400 text-sm">{formatTimeOnly(lead.created_at)}</td>
                          <td className="py-3 px-4">
                            <Badge className="bg-blue-500/20 text-blue-400">
                              {lead.visit_count || 1} {(lead.visit_count || 1) === 1 ? 'visit' : 'visits'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            {lead.rating ? (
                              <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                  <Star 
                                    key={i}
                                    className={`w-3 h-3 ${i < lead.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`}
                                  />
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-600">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {(lead.feedback_liked || lead.feedback_improve) ? (
                              <div className="max-w-[200px]">
                                {lead.feedback_liked && (
                                  <p className="text-green-400 text-xs truncate" title={lead.feedback_liked}>
                                    👍 {lead.feedback_liked}
                                  </p>
                                )}
                                {lead.feedback_improve && (
                                  <p className="text-amber-400 text-xs truncate" title={lead.feedback_improve}>
                                    💡 {lead.feedback_improve}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-600 text-sm">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* TDS Registration Tab */}
        {activeTab === 'tds-reg' && (
          <Card className="bg-[#111827] border-white/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-cyan-400" />
                  TDS Simulator Registrations
                  <Badge className="bg-cyan-500/20 text-cyan-400 ml-2">{tdsLeads.length} users</Badge>
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {tdsLeads.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No TDS registrations yet. Users will appear here after registering for TDS Form filing.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">#</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Name</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Email</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Form Type</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Date</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Visits</th>
                        <th className="text-left text-gray-400 text-sm font-medium py-3 px-4">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tdsLeads
                        .filter(lead => 
                          !searchTerm || 
                          lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          lead.name?.toLowerCase().includes(searchTerm.toLowerCase())
                        )
                        .map((lead, index) => (
                          <tr key={lead._id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-3 px-4 text-gray-500">{index + 1}</td>
                            <td className="py-3 px-4 text-white">{lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || '-'}</td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <span className="text-cyan-400">{lead.email}</span>
                                <button
                                  onClick={() => copyToClipboard(lead.email, lead._id)}
                                  className="text-gray-500 hover:text-white"
                                >
                                  {copiedId === lead._id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                                </button>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex flex-wrap gap-1">
                                {(lead.forms_accessed || [lead.formType]).filter(Boolean).map((form, i) => (
                                  <Badge key={i} className="bg-cyan-500/20 text-cyan-400 text-xs">
                                    Form {form}
                                  </Badge>
                                ))}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-gray-400">
                              {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-IN', { 
                                day: '2-digit', 
                                month: 'short', 
                                year: 'numeric' 
                              }) : '-'}
                            </td>
                            <td className="py-3 px-4">
                              <Badge className="bg-white/10 text-white">{lead.visit_count || 1}</Badge>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => window.open(`mailto:${lead.email}`, '_blank')}
                                  className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                                >
                                  <Mail className="w-4 h-4" />
                                </Button>
                                {lead.activity_log && lead.activity_log.length > 0 && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedLead(lead);
                                      setShowActivityModal(true);
                                    }}
                                    className="border-white/20 text-white hover:bg-white/10"
                                  >
                                    <Calendar className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* GSTR-1 Tab */}
        {activeTab === 'gstr1' && (
          <Card className="bg-[#111827] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-400" />
                GSTR-1 Subscribers
                <Badge className="bg-blue-500/20 text-blue-400 ml-2">{gstr1Payments.length} users</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {gstr1Payments.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No GSTR-1 subscribers yet</p>
              ) : (
                <div className="space-y-3">
                  {gstr1Payments.map((payment) => (
                    <PaymentCard key={payment._id} payment={payment} onSend={handleSendCredentials} sending={sendingCredentials} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* GSTR-3B Tab */}
        {activeTab === 'gstr3b' && (
          <Card className="bg-[#111827] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-purple-400" />
                GSTR-3B Subscribers
                <Badge className="bg-purple-500/20 text-purple-400 ml-2">{gstr3bPayments.length} users</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {gstr3bPayments.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No GSTR-3B subscribers yet</p>
              ) : (
                <div className="space-y-3">
                  {gstr3bPayments.map((payment) => (
                    <PaymentCard key={payment._id} payment={payment} onSend={handleSendCredentials} sending={sendingCredentials} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Both Tab */}
        {activeTab === 'both' && (
          <Card className="bg-[#111827] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-amber-400" />
                Bundle Subscribers (Both GSTR-1 & 3B)
                <Badge className="bg-amber-500/20 text-amber-400 ml-2">{bothPayments.length} users</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {bothPayments.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No bundle subscribers yet</p>
              ) : (
                <div className="space-y-3">
                  {bothPayments.map((payment) => (
                    <PaymentCard key={payment._id} payment={payment} onSend={handleSendCredentials} sending={sendingCredentials} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Feedback Modal */}
        <Dialog open={showFeedbackModal} onOpenChange={setShowFeedbackModal}>
          <DialogContent className="bg-[#111827] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-teal-400" />
                Add Feedback
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Add feedback for {selectedLead?.email}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Rating (Optional)</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setFeedbackRating(star)}
                      className="p-2 hover:bg-white/10 rounded"
                    >
                      <Star
                        className={`w-6 h-6 ${
                          star <= feedbackRating
                            ? 'text-amber-400 fill-amber-400'
                            : 'text-gray-600'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Feedback Notes</label>
                <Textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Enter feedback or notes about this user..."
                  className="bg-white/5 border-white/10 text-white min-h-[100px]"
                />
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowFeedbackModal(false)} className="border-white/20 text-white">
                Cancel
              </Button>
              <Button onClick={handleAddFeedback} className="bg-teal-600 hover:bg-teal-500">
                Save Feedback
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Activity Log Modal */}
        <Dialog open={showActivityModal} onOpenChange={setShowActivityModal}>
          <DialogContent className="bg-[#111827] border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-400" />
                Activity Log
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Visit history for {selectedLead?.email}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-4 max-h-[400px] overflow-y-auto">
              {selectedLead?.activity_log?.map((activity, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-400 text-xs font-medium">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-sm">{activity.action || 'GST Registration'}</p>
                    <p className="text-gray-500 text-xs">{formatDate(activity.timestamp)}</p>
                  </div>
                </div>
              ))}
              {(!selectedLead?.activity_log || selectedLead?.activity_log.length === 0) && (
                <p className="text-gray-500 text-center py-4">No activity log available</p>
              )}
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setShowActivityModal(false)} className="border-white/20 text-white">
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// Payment Card Component
const PaymentCard = ({ payment, onSend, sending }) => {
  const [showPwd, setShowPwd] = useState(false);
  
  return (
    <div className="p-4 bg-white/5 rounded-lg border border-white/10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-teal-500/20 rounded-lg flex items-center justify-center">
            <Mail className="w-5 h-5 text-teal-400" />
          </div>
          <div>
            <p className="text-white font-medium">{payment.email}</p>
            <div className="flex items-center gap-2 text-sm">
              <Phone className="w-3 h-3 text-gray-500" />
              <span className="text-gray-500">{payment.phone || 'N/A'}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm text-gray-400">Plan</p>
            <p className="text-white font-medium">{payment.planName || payment.plan}</p>
          </div>
          
          <div className="text-right">
            <p className="text-sm text-gray-400">Amount</p>
            <p className="text-teal-400 font-medium flex items-center">
              <IndianRupee className="w-3 h-3" />
              {payment.amount}
            </p>
          </div>
          
          <div className="text-right">
            <p className="text-sm text-gray-400">UTR</p>
            <p className="text-white font-mono text-sm">{payment.utrNumber || 'N/A'}</p>
          </div>
          
          {payment.generated_password && (
            <div className="text-right">
              <p className="text-sm text-gray-400">Password</p>
              <div className="flex items-center gap-2">
                <p className="text-amber-400 font-mono text-sm">
                  {showPwd ? payment.generated_password : '••••••••'}
                </p>
                <button onClick={() => setShowPwd(!showPwd)} className="text-gray-500 hover:text-white">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}
          
          <Badge className={payment.status === 'verified' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}>
            {payment.status === 'verified' ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Verified</> : <><Clock className="w-3 h-3 mr-1" /> Pending</>}
          </Badge>
          
          {payment.status !== 'verified' && (
            <Button
              size="sm"
              onClick={() => onSend(payment._id)}
              disabled={sending[payment._id]}
              className="bg-teal-600 hover:bg-teal-500"
            >
              {sending[payment._id] ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4 mr-1" />
                  Send
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
